import { FormEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Divider, TextField, Alert } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getErrorMessage } from "../api/client";
import AuthCard from "../components/AuthCard";
import GoogleLogo from "../components/GoogleLogo";
import TotpInput from "../components/TotpInput";

// `next` is consumed both by this page and by the MCP OAuth continuation flow
// (`/mcp/login`). To prevent open-redirect abuse, accept only same-origin paths
// under `/mcp/`. A protocol-relative `//evil.com` would otherwise be treated as
// same-origin by the browser.
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/mcp/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  return raw;
}

function followNext(next: string) {
  // `next` always points at a backend route (e.g. `/mcp/login?...`), not an
  // SPA route, so do a full-page navigation rather than client-side routing.
  window.location.assign(next);
}

const SSO_ERROR_MESSAGES: Record<string, string> = {
  cancelled: "Sign-in with Google was cancelled.",
  invalid_request: "Sign-in request was invalid. Please try again.",
  state_mismatch: "Sign-in session expired. Please try again.",
  invalid_state: "Sign-in session expired. Please try again.",
  token_exchange_failed: "Could not exchange Google sign-in code.",
  missing_id_token: "Google did not return an identity token.",
  invalid_id_token: "Google identity token could not be verified.",
  email_not_verified: "Your Google email is not verified.",
  not_a_workspace_account:
    "Only Google Workspace accounts are allowed. Personal Gmail accounts can't sign in here.",
  no_org_for_domain:
    "No organization is configured for your Google Workspace domain. Contact your administrator.",
  account_disabled: "This account has been disabled. Contact your administrator.",
  account_conflict:
    "Your Google account does not match the identity on file for this email. Contact your administrator.",
  not_configured: "Single sign-on is not enabled in this environment.",
};

export default function LoginPage() {
  const { login, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get("next"));
  const ssoErrorCode = searchParams.get("sso_error");
  const ssoErrorMessage = ssoErrorCode
    ? SSO_ERROR_MESSAGES[ssoErrorCode] ?? "Google sign-in failed."
    : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    if (isLoading || !user) return;
    if (next) {
      followNext(next);
    } else {
      navigate("/", { replace: true });
    }
  }, [isLoading, user, next, navigate]);

  const loginMutation = useMutation({
    mutationFn: (totp?: string) => login(email, password, totp),
    meta: { suppressErrorToast: true },
    onSuccess: (result) => {
      if (result.mfaRequired) {
        setMfaRequired(true);
        setTotpCode("");
      } else if (next) {
        followNext(next);
      } else {
        navigate("/", { replace: true });
      }
    },
    onError: (_, totp) => {
      if (totp !== undefined) setTotpCode("");
    },
  });
  const loading = loginMutation.isPending;
  const error = loginMutation.error ? getErrorMessage(loginMutation.error) : "";

  useEffect(() => {
    if (mfaRequired && totpCode.replace(/\s/g, "").length === 6) {
      loginMutation.mutate(totpCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpCode, mfaRequired]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mfaRequired) return;
    loginMutation.mutate(undefined);
  }

  return (
    <AuthCard
      title={mfaRequired ? "Two-step verification" : "Sign in to your account"}
      subtitle={mfaRequired ? "Enter your authenticator code" : undefined}
    >
      {ssoErrorMessage && !error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ssoErrorMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {!mfaRequired && (
          <>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {mfaRequired && (
          <TotpInput value={totpCode} onChange={setTotpCode} autoFocus disabled={loading} />
        )}

        <Button type="submit" variant="contained" fullWidth loading={loading} sx={{ mt: 1 }}>
          Sign in
        </Button>
      </Box>

      {!mfaRequired && (
        <>
          <Divider sx={{ my: 3 }}>or</Divider>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              window.location.assign(
                `/api/v1/auth/google/start?redirect_to=${encodeURIComponent(next ?? "/")}`,
              );
            }}
            startIcon={<GoogleLogo />}
          >
            Sign in with Google
          </Button>
        </>
      )}
    </AuthCard>
  );
}
